import { Button, Image } from '@outdoor-voices/ui';
import { lockScroll, unlockScroll } from '@outdoor-voices/ui/dist/utils/dom/scroll';
import cc from 'classcat';
import NextImage from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import useKeyFocus from '@outdoor-voices/ui/dist/utils/hooks/useKeyFocus';

import { useLoyaltyActionCreateMutation } from '../../generated/graphql';
import css from './campaigns.module.scss';
import overlayCSS from './overlay.module.scss';
import XLarge from '../icons/XLarge';

type Campaign = {
  actionName: string;
  createActionOnURLVisit: boolean;
  ctaText: string;
  details: string;
  displayOrder: number;
  id: number;
  icon: string;
  maxCompletionsPerUser?: number;
  title: string;
  rewardText: string;
  status?: {
    customerTimesCompleted: number;
  };
  url: string;
};
type Props = {
  campaigns: Campaign[];
  loggedIn?: boolean;
  minimal?: boolean;
  title: string;
};

let elementToReturnFocusTo = null;

const isCompletedByName = (campaign: any, minimal: boolean) =>
  campaign.title.toLowerCase() === 'create an account' && minimal;
const isCompletedByStatus = (campaign: any) =>
  campaign.status &&
  campaign.maxCompletionsPerUser &&
  campaign.status.customerTimesCompleted >= campaign.maxCompletionsPerUser;

const Campaigns: React.FC<Props> = ({ campaigns, loggedIn = false, minimal, title }) => {
  const overlayRef = useRef(null);
  const [campaignIdx, setCampaignIdx] = useState(0);
  const { open, setOpen } = useKeyFocus(false, overlayRef);
  const [clicked, setClicked] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [mutate] = useLoyaltyActionCreateMutation();

  const onClose = () => {
    setOpen(false);
  };

  const onOpen = (idx: number) => {
    setCampaignIdx(idx);
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      // Track the currently focused element so we can return to it on close.
      elementToReturnFocusTo = document.activeElement;
      lockScroll();
    } else {
      unlockScroll();
      if (elementToReturnFocusTo) {
        elementToReturnFocusTo.focus();
      }
    }
  }, [open]);

  useEffect(() => {
    if (mounted) {
      return;
    }
    setMounted(true);
  }, [mounted]);

  // const navigate = (url: string) => {
  //   if (window.safari) {
  //     window.location.assign(url);
  //     return;
  //   }
  //   window.open(url);
  // };

  const onCampaignClick = async (event: { preventDefault: () => void; metaKey: any }) => {
    if (clicked) {
      return;
    }

    const campaign = campaigns[campaignIdx];
    const { actionName, createActionOnURLVisit, url } = campaign;

    if (createActionOnURLVisit) {
      setClicked(true);
      event.preventDefault();

      // A hack for safari with async functions that open new windows
      const windowReference = window.open();

      await mutate({ variables: { actionName } });

      setOpen(false);
      setClicked(false);

      windowReference.location = url;
    }
  };

  const tabIndex = open ? 0 : -1;
  const campaign = campaigns[campaignIdx];
  const closeLabel = 'Close dialog';
  const displayCampaignCompleted = isCompletedByName(campaign, minimal) || isCompletedByStatus(campaign);

  return (
    <section className={cc([css.root, { [css.padded]: !minimal }])} id="ways-to-score">
      <h2 className={cc([css.title, { [css.minimal]: minimal }])}>
        {minimal ? (
          title
        ) : (
          <NextImage
            alt={title}
            height="164"
            layout="responsive"
            objectFit="contain"
            src="/loyalty/ways-to-score.png"
            width="1001"
            priority
          />
        )}
      </h2>
      <ul className={cc([css.list, { [css.minimal]: minimal }])}>
        {campaigns.map((campaign, idx) => {
          const { id, icon, title, maxCompletionsPerUser, rewardText, status } = campaign;
          const displayCompleted = isCompletedByName(campaign, minimal) || isCompletedByStatus(campaign);
          const kid = (
            <>
              <div className={css.image} role="presentation">
                <Image wrap={false} alt={title} src={icon} height={300} width={300} sizes={[80, 120, 200]} />
              </div>
              <h3 className={css.itemTitle}>{title}</h3>
              {!displayCompleted && <p className={css.points}>+{rewardText}</p>}
              {displayCompleted && <p className={css.points}>Completed</p>}
            </>
          );
          return (
            <li className={css.item} key={id}>
              {loggedIn && (
                <button
                  aria-label={`Click to learn more about ${title}`}
                  className={css.button}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(idx);
                  }}
                  title={`Max: ${maxCompletionsPerUser} Completed: ${status?.customerTimesCompleted}`}
                >
                  {kid}
                </button>
              )}
              {!loggedIn && <>{kid}</>}
            </li>
          );
        })}
      </ul>
      {mounted && (
        <>
          <div aria-hidden={open ? 'false' : 'true'} ref={overlayRef}>
            <div
              aria-describedby="campaign-details"
              aria-labelledby="campaign-title"
              aria-modal="true"
              className={cc([overlayCSS.overlay, { [overlayCSS.on]: open }])}
              role="dialog"
            >
              <button
                aria-label={closeLabel}
                className={overlayCSS.close}
                onClick={onClose}
                tabIndex={tabIndex}
                title={closeLabel}
              >
                <XLarge />
              </button>
              <div className={css.campaignImage} role="presentation">
                <Image wrap={false} alt={title} src={campaign.icon} height={300} width={300} sizes={[80, 120, 200]} />
              </div>
              <h3 className={css.campaignTitle} id="campaign-title">
                {campaign.title}
              </h3>
              {displayCampaignCompleted && <p className={cc([css.points, css.modal])}>Completed</p>}
              <p className={css.campaignDetails} id="campaign-details">
                {campaign.details}
              </p>
              {!displayCampaignCompleted && campaign.ctaText && (
                <Button
                  ariaLabel={`Click to ${campaign.ctaText}`}
                  disabled={clicked}
                  href={campaign.url}
                  onClick={onCampaignClick}
                  tabIndex={tabIndex}
                  targetBlank
                >
                  {campaign.ctaText}
                </Button>
              )}
            </div>
            <button
              aria-label={closeLabel}
              className={cc([overlayCSS.bg, { [overlayCSS.on]: open }])}
              onClick={onClose}
              tabIndex={tabIndex}
              title={closeLabel}
            ></button>
          </div>
          {/* <div aria-hidden="true" className={css.ghost} role="presentation"></div> */}
        </>
      )}
    </section>
  );
};
export default Campaigns;
